/**
 * Keeps the licence current in a tab nobody reloads.
 *
 * The register of modules is read once when the portal starts (stores/app.ts,
 * `loadModules`), which was right while the only way it could change was a new
 * contract and a new sign-in. It is not right any more: the vendor switches a module
 * on for a hotel whose reception has had the same tab open since Monday, and until
 * somebody signs out again that desk is looking at a portal that does not have it.
 * The other direction is worse — a module switched OFF stays drawn, and every press
 * of it is refused by the service with a 403 the screen has no sentence for.
 *
 * So the licence is re-read on the three occasions that cost nothing:
 *
 *   - the tab becomes visible again, which is what happens when the person comes back
 *     from the mail client or the booking engine;
 *   - the window takes focus, for the desks that keep the portal beside another
 *     window rather than behind it;
 *   - and every five minutes regardless, for the screen in the back office that is
 *     watched all day and never touched.
 *
 * It is deliberately quiet. No toast, no loader, no error when the read fails: a
 * licence that could not be re-read is the licence we already had, and telling a
 * receptionist about a background request is telling her about our plumbing. The two
 * event sources fire together on a tab switch in most browsers, so a read is not
 * repeated inside `MIN_GAP_MS` and never runs twice at once.
 *
 * When the refreshed answer no longer holds the module the CURRENT screen needs, this
 * is also what leaves it. The router's guard would catch it on the next navigation
 * anyway, but "the next navigation" can be an hour away, and in that hour the screen
 * is a working-looking one whose every button is refused.
 */
import { onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

import { useAppStore } from '@/stores/app'

/** How often the licence is re-read in a tab left open. */
const EVERY_MS = 5 * 60 * 1000

/** The shortest gap between two reads, so focus and visibility do not both fire one. */
const MIN_GAP_MS = 30 * 1000

export const useModuleRefresh = (): void => {
    if (typeof window === 'undefined') return

    const appStore = useAppStore()
    const router   = useRouter()

    let lastRead = 0
    let reading  = false

    const refresh = async (): Promise<void> => {
        // Only while somebody is signed in: the read carries the account's token, and
        // the login screen has no licence to keep current.
        if (reading || !appStore.isLoggedIn()) return

        const now = Date.now()

        if (now - lastRead < MIN_GAP_MS) return

        reading  = true
        lastRead = now

        try {
            await appStore.loadModules(true)
        } finally {
            reading = false
        }

        const module = router.currentRoute.value.meta.module as string | undefined

        if (!module || appStore.hasModule(module)) return

        // Replace rather than push: the screen that has just stopped existing does not
        // belong in the history a Back button walks through.
        void router.replace({ name: 'module-off', query: { module } })
    }

    const onVisible = (): void => {
        if (document.visibilityState === 'visible') void refresh()
    }

    const onFocus = (): void => { void refresh() }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    const timer = window.setInterval(() => { void refresh() }, EVERY_MS)

    onUnmounted(() => {
        document.removeEventListener('visibilitychange', onVisible)
        window.removeEventListener('focus', onFocus)
        window.clearInterval(timer)
    })
}
